import { useEffect, useState, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { Servicio } from '@/components/hooks/useServicios';
import { useHorariosPorEmpresa } from '@/components/hooks/useHorarios';
import {
  useDiccionario,
  useDescripcionTabla,
} from '@/components/hooks/useDiccionario';

const DAYS = [
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
  'domingo',
];

// ============================================================
// Tipo para categorías de servicio
// ============================================================
export type CategoriaServicio = {
  id_categoria_servicio: string;
  nombre: string;
  imagen?: string | null;
  icono?: string | null;
  orden?: number;
  activo: boolean;
};

const FORM_VACIO = {
  servicio: '',
  descripcion: '',
  precio: '0',
  precio_aplica: 'Por servicio',
  duracion_minutos: '30',
  activo: true,
  servicio_publico: true,
  cliente_min: '1',
  cliente_max: '1',
  capacidad_concurrente: '1',
  permite_extension: false,
  permite_grupos: false,
  requiere_trabajador: true,
  dias_semana: [] as string[],
  horario_concreto: false,
  reserva_por_horas: false,
  reservable_online: true,
  horas_min: '1',
  horas_max: '4',
  id_categoria_servicio: null as string | null,
  icono: null as string | null,
  orden: '0',
};

export function useEditarServicios(
  open: boolean,
  editingService: Servicio | null,
  empresaId: string,
  onSaved: (newServiceId?: string) => void,
  onClose: () => void,
  onEditPhases?: (serviceId: string) => void
) {
  // ============================================================
  // Estado principal del formulario
  // ============================================================
  const [form, setForm] = useState(FORM_VACIO);

  const [crearFasesAhora, setCrearFasesAhora] = useState(false);
  const [precioAplicaOptions, setPrecioAplicaOptions] = useState<string[]>([]);
  const [loadingForm, setLoadingForm] = useState(false);
  const [horas, setHoras] = useState<string[]>([]);
  const [newHora, setNewHora] = useState('');
  const [categoriasServicio, setCategoriasServicio] = useState<
    CategoriaServicio[]
  >([]);
  const [loadingImagen, setLoadingImagen] = useState(false);

  const horariosEmpresa = useHorariosPorEmpresa(empresaId);
  const idUsuario = localStorage.getItem('id_usuario');

  const { campos } = useDiccionario('servicios');
  const { descripcion: descripcionServicioDias } =
    useDescripcionTabla('servicio_dias');

  // ============================================================
  // Info dialogs y secciones
  // ============================================================
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDays, setShowDays] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showInfoPublico, setShowInfoPublico] = useState(false);
  const [showInfoActivo, setShowInfoActivo] = useState(false);
  const [showInfoPermiteGrupos, setShowInfoPermiteGrupos] = useState(false);
  const [showInfoRequiereTrabajador, setShowInfoRequiereTrabajador] =
    useState(false);
  const [showInfoDiasDisponibles, setShowInfoDiasDisponibles] = useState(false);
  const [showInfoHorarioConcreto, setShowInfoHorarioConcreto] = useState(false);

  const daysRef = useRef<HTMLDivElement>(null);
  const advancedRef = useRef<HTMLDivElement>(null);
  const horariosRef = useRef<HTMLDivElement>(null);

  const getDescripcion = (columna: string) =>
    campos.find((c) => c.columna === columna)?.descripcion_columna || '';

  // ============================================================
  // Funciones auxiliares del formulario
  // ============================================================
  const toggleDay = (day: string) => {
    setForm((prev) => ({
      ...prev,
      dias_semana: prev.dias_semana.includes(day)
        ? prev.dias_semana.filter((d) => d !== day)
        : [...prev.dias_semana, day],
    }));
  };

  const handleInput = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // ============================================================
  // Cargar categorías de servicio de la empresa
  // ============================================================
  const loadCategoriasServicio = useCallback(async () => {
    if (!empresaId) return;

    const { data, error } = await supabase
      .from('categoria_servicio')
      .select('id_categoria_servicio, nombre, imagen, icono, orden, activo')
      .eq('id_empresa', empresaId)
      .eq('activo', true)
      .order('orden', { ascending: true });

    if (error) {
      Sentry.captureException(error, { extra: { empresaId } }); // 👈
    } else if (data) {
      setCategoriasServicio(data as CategoriaServicio[]);
    }
  }, [empresaId]);

  // ============================================================
  // Crear nueva categoría de servicio
  // ============================================================
  const crearCategoriaServicio = async (
    nombre: string
  ): Promise<CategoriaServicio | null> => {
    if (!nombre.trim()) return null;

    const { data, error } = await supabase
      .from('categoria_servicio')
      .insert([
        {
          id_empresa: empresaId,
          nombre: nombre.trim(),
          activo: true,
          usuario_creacion: idUsuario,
        },
      ])
      .select('id_categoria_servicio, nombre, imagen, icono, orden, activo')
      .single();

    if (error) {
      Sentry.captureException(error, { extra: { empresaId, nombre } }); // 👈
      toast.error('Error al crear la categoría.');
      return null;
    }

    await loadCategoriasServicio();
    return data as CategoriaServicio;
  };

  // ============================================================
  // Comprimir imagen via canvas antes de subir
  // ============================================================
  const comprimirImagen = (file: File): Promise<File> =>
    new Promise((resolve, reject) => {
      const MAX_PX = 800;
      const QUALITY = 0.82;

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        let { width, height } = img;
        if (width > MAX_PX || height > MAX_PX) {
          if (width >= height) {
            height = Math.round((height * MAX_PX) / width);
            width = MAX_PX;
          } else {
            width = Math.round((width * MAX_PX) / height);
            height = MAX_PX;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('canvas toBlob failed'));
              return;
            }
            resolve(new File([blob], 'categoria.jpg', { type: 'image/jpeg' }));
          },
          'image/jpeg',
          QUALITY
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image load error'));
      };

      img.src = objectUrl;
    });

  // ============================================================
  // Subir imagen de categoría
  // ============================================================
  const uploadImagenCategoria = async (
    file: File,
    id_categoria_servicio: string
  ): Promise<string | null> => {
    if (!file || !id_categoria_servicio) return null;

    try {
      setLoadingImagen(true);

      const fileComprimido = await comprimirImagen(file);
      const path = `categoria_empresa/${empresaId}/${id_categoria_servicio}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('bookoll')
        .upload(path, fileComprimido, {
          upsert: true,
          cacheControl: '0',
          contentType: 'image/jpeg',
        });

      if (uploadError) throw uploadError;

      // Misma ruta en cada subida: se versiona la URL para saltar la caché.
      const publicUrl = `${
        supabase.storage.from('bookoll').getPublicUrl(path).data.publicUrl
      }?v=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('categoria_servicio')
        .update({ imagen: publicUrl, usuario_edicion: idUsuario })
        .eq('id_categoria_servicio', id_categoria_servicio);

      if (updateError) throw updateError;

      await loadCategoriasServicio();
      toast.success('Imagen de categoría actualizada.');
      return publicUrl;
    } catch (err) {
      Sentry.captureException(err, {
        extra: { empresaId, id_categoria_servicio },
      }); // 👈
      toast.error('Error al subir la imagen de la categoría.');
      return null;
    } finally {
      setLoadingImagen(false);
    }
  };

  // ============================================================
  // Eliminar imagen de categoría
  // ============================================================
  const deleteImagenCategoria = async (
    id_categoria_servicio: string
  ): Promise<void> => {
    if (!id_categoria_servicio) return;

    try {
      setLoadingImagen(true);

      const path = `categoria_empresa/${empresaId}/${id_categoria_servicio}.jpg`;

      const { error: removeError } = await supabase.storage
        .from('bookoll')
        .remove([path]);

      if (removeError) throw removeError;

      const { error: updateError } = await supabase
        .from('categoria_servicio')
        .update({ imagen: null, usuario_edicion: idUsuario })
        .eq('id_categoria_servicio', id_categoria_servicio);

      if (updateError) throw updateError;

      await loadCategoriasServicio();
      toast.success('Imagen de categoría eliminada.');
    } catch (err) {
      Sentry.captureException(err, {
        extra: { empresaId, id_categoria_servicio },
      }); // 👈
      toast.error('No se pudo eliminar la imagen de la categoría.');
    } finally {
      setLoadingImagen(false);
    }
  };

  // ============================================================
  // Cargar datos iniciales
  // ============================================================
  useEffect(() => {
    if (!open) return;

    setLoadingForm(true);
    setCrearFasesAhora(false);

    const loadPrecioAplica = async () => {
      const { data, error } = await supabase
        .from('vista_cb_precio_aplica')
        .select('precio_aplica');

      if (error) {
        Sentry.captureException(error, { extra: { empresaId } }); // 👈
      } else if (data) {
        setPrecioAplicaOptions(data.map((d) => d.precio_aplica));
      }
    };

    const loadDias = async (id_servicio: string) => {
      const { data, error } = await supabase
        .from('servicio_dias')
        .select('dia_semana')
        .eq('id_servicio', id_servicio)
        .eq('activo', true);

      if (error) {
        Sentry.captureException(error, { extra: { id_servicio } }); // 👈
      } else if (data) {
        setForm((prev) => ({
          ...prev,
          dias_semana: data.map((d) => d.dia_semana),
        }));
      }
    };

    loadPrecioAplica();
    loadCategoriasServicio();

    if (editingService) {
      setForm({
        servicio: editingService.servicio,
        descripcion: editingService.descripcion || '',
        precio: editingService.precio?.toString() || '',
        precio_aplica: editingService.precio_aplica || 'Por servicio',
        duracion_minutos: editingService.duracion_minutos?.toString() || '',
        activo: editingService.activo,
        servicio_publico: editingService.servicio_publico,
        cliente_min: editingService.cliente_min?.toString() || '1',
        cliente_max: editingService.cliente_max?.toString() || '1',
        capacidad_concurrente:
          editingService.capacidad_concurrente?.toString() || '1',
        permite_extension: editingService.permite_extension,
        permite_grupos: editingService.permite_grupos,
        requiere_trabajador: editingService.requiere_trabajador,
        dias_semana: [],
        horario_concreto: editingService.horario_concreto ?? false,
        reserva_por_horas: editingService.reserva_por_horas ?? false,
        reservable_online: editingService.reservable_online ?? true,
        horas_min: editingService.horas_min?.toString() ?? '1',
        horas_max: editingService.horas_max?.toString() ?? '4',
        id_categoria_servicio:
          (editingService as any).id_categoria_servicio ?? null,
        icono: (editingService as any).icono ?? null,
        orden: (editingService as any).orden?.toString() ?? '0',
      });

      loadDias(editingService.id_servicio);

      if (editingService.horarios_servicio?.length) {
        const mapped = editingService.horarios_servicio.map((h) => {
          const start = h.hora.slice(0, 5);
          const dur = parseInt(
            editingService.duracion_minutos?.toString() || '0',
            10
          );
          const end = new Date();
          const [hh, mm] = start.split(':').map(Number);
          end.setHours(hh, mm + dur, 0, 0);
          return `${start} - ${end.toTimeString().slice(0, 5)}`;
        });
        setHoras(mapped);
      } else {
        setHoras([]);
      }
    } else {
      setForm(FORM_VACIO);
      setHoras([]);
    }

    const id = setTimeout(() => setLoadingForm(false), 200);
    return () => clearTimeout(id);
  }, [open, editingService]);

  // Recalcular horas si cambia la duración
  useEffect(() => {
    const duracion = parseInt(form.duracion_minutos, 10);
    if (isNaN(duracion) || duracion <= 0) return;

    setHoras((prev) =>
      prev.map((h) => {
        const [ini] = h.split(' - ');
        const [hh, mm] = ini.split(':').map(Number);
        const start = new Date();
        start.setHours(hh, mm, 0, 0);
        const end = new Date(start.getTime() + duracion * 60000);
        return `${ini} - ${end.toTimeString().slice(0, 5)}`;
      })
    );
  }, [form.duracion_minutos]);

  // ============================================================
  // Guardar servicio (crear o editar)
  // ============================================================
  const handleSubmit = async () => {
    const {
      servicio,
      descripcion,
      precio,
      precio_aplica,
      duracion_minutos,
      activo,
      servicio_publico,
      cliente_min,
      cliente_max,
      capacidad_concurrente,
      permite_extension,
      permite_grupos,
      requiere_trabajador,
      dias_semana,
      horario_concreto,
      reserva_por_horas,
      reservable_online,
      horas_min,
      horas_max,
      id_categoria_servicio,
      icono,
      orden,
    } = form;

    if (!servicio) {
      toast.error('Debes introducir un nombre para el servicio.');
      return;
    }
    if (parseInt(cliente_min, 10) > parseInt(cliente_max, 10)) {
      toast.error('El mínimo de clientes no puede ser mayor que el máximo.');
      return;
    }
    const hMin = parseInt(horas_min, 10);
    const hMax = parseInt(horas_max, 10);
    if (reserva_por_horas && (!(hMin >= 1) || !(hMax >= hMin))) {
      toast.error(
        'Revisa las horas: el mínimo debe ser 1 o más y no puede superar al máximo.'
      );
      return;
    }

    const payload = {
      servicio,
      descripcion,
      precio: parseFloat(precio),
      precio_aplica,
      // En servicios por horas la duración base es 1 h; la real la elige el
      // cliente y queda en la hora_fin de la cita.
      duracion_minutos: reserva_por_horas
        ? 60
        : duracion_minutos
        ? parseInt(duracion_minutos, 10)
        : null,
      reserva_por_horas,
      reservable_online,
      horas_min: reserva_por_horas ? hMin : null,
      horas_max: reserva_por_horas ? hMax : null,
      activo,
      servicio_publico,
      cliente_min: parseInt(cliente_min, 10),
      cliente_max: parseInt(cliente_max, 10),
      capacidad_concurrente: parseInt(capacidad_concurrente, 10),
      permite_extension,
      permite_grupos,
      requiere_trabajador,
      horario_concreto,
      id_categoria_servicio: id_categoria_servicio || null,
      icono: icono || null,
      orden: parseInt(orden, 10) || 0,
    };

    let idServicio = editingService?.id_servicio;

    const op = editingService
      ? supabase
          .from('servicios')
          .update({ ...payload, usuario_edicion: idUsuario })
          .eq('id_servicio', idServicio!)
      : supabase
          .from('servicios')
          .insert([
            { ...payload, id_empresa: empresaId, usuario_creacion: idUsuario },
          ])
          .select('id_servicio')
          .single();

    const { error, data } = await op;

    if (error) {
      Sentry.captureException(error, {
        // 👈
        extra: { empresaId, idServicio, editingService: !!editingService },
      });
      toast.error('Error al guardar el servicio.');
      return;
    }

    if (!editingService) {
      if (!data) {
        toast.error('Error: no se recibió el id del servicio.');
        return;
      }
      idServicio = data.id_servicio;
    }

    // Guardar días
    const { error: deleteDiasError } = await supabase
      .from('servicio_dias')
      .delete()
      .eq('id_servicio', idServicio);

    if (deleteDiasError) {
      Sentry.captureException(deleteDiasError, { extra: { idServicio } }); // 👈
    }

    if (dias_semana.length > 0) {
      const { error: insertDiasError } = await supabase
        .from('servicio_dias')
        .insert(
          dias_semana.map((dia) => ({
            id_servicio: idServicio,
            dia_semana: dia,
            activo: true,
            usuario_creacion: idUsuario,
          }))
        );

      if (insertDiasError) {
        Sentry.captureException(insertDiasError, { extra: { idServicio } }); // 👈
      }
    }

    // Guardar horarios específicos
    const { error: deleteHorariosError } = await supabase
      .from('servicio_horarios')
      .delete()
      .eq('id_servicio', idServicio);

    if (deleteHorariosError) {
      Sentry.captureException(deleteHorariosError, { extra: { idServicio } }); // 👈
    }

    if (horario_concreto && horas.length > 0) {
      const { error: insertHorariosError } = await supabase
        .from('servicio_horarios')
        .insert(
          horas.map((h) => ({
            id_servicio: idServicio,
            hora: h.split(' - ')[0],
            activo: true,
            usuario_creacion: idUsuario,
          }))
        );

      if (insertHorariosError) {
        Sentry.captureException(insertHorariosError, { extra: { idServicio } }); // 👈
      }
    }

    toast.success(
      editingService
        ? 'Servicio actualizado correctamente.'
        : 'Servicio creado correctamente.'
    );

    // ── Caso especial: crear fases inmediatamente ───────────────────────────
    if (!editingService && crearFasesAhora && idServicio && onEditPhases) {
      onSaved(idServicio);
      onEditPhases(idServicio);
      onClose();
      return;
    }

    // ── Edición: cerrar directamente ────────────────────────────────────────
    if (editingService) {
      onSaved();
      onClose();
      return;
    }

    // ── Creación: pasar el ID al padre ──────────────────────────────────────
    onSaved(idServicio);

    if (!requiere_trabajador) {
      setShowInfo(true);
    }
  };

  // ============================================================
  // Horas concretas
  // ============================================================
  const addHora = () => {
    if (!newHora) return;

    const duracion = parseInt(form.duracion_minutos, 10);
    if (isNaN(duracion) || duracion <= 0) {
      toast.warning(
        'Sin duración definida, el horario se añadirá sin hora de fin.'
      );
    }

    const [h, m] = newHora.split(':').map(Number);
    const start = new Date();
    start.setHours(h, m, 0, 0);
    const end = new Date(start.getTime() + duracion * 60000);
    const finStr = end.toTimeString().slice(0, 5);

    if (!validarHoraPorDia(newHora, finStr)) {
      toast.warning(
        'Este horario no encaja en el horario laboral según los días seleccionados.'
      );
    }

    if (horas.some((h) => h.startsWith(newHora))) {
      toast.error('Ya existe un horario con esa hora de inicio.');
      return;
    }

    const solapa = horas.some((h) => {
      const [ini, fin] = h.split(' - ');
      return (
        (newHora >= ini && newHora < fin) || (finStr > ini && finStr <= fin)
      );
    });

    if (solapa) {
      toast.error('Este horario se solapa con otro existente.');
      return;
    }

    setHoras((prev) => [...prev, `${newHora} - ${finStr}`]);
    setNewHora('');
  };

  const validarHoraEnHorarioEmpresa = (
    horaInicio: string,
    horaFin: string
  ): boolean => {
    const horarios = horariosEmpresa?.horarios || [];
    if (!horarios.length) return true;
    const toMin = (h: string) => {
      const [hh, mm] = h.split(':').map(Number);
      return hh * 60 + mm;
    };
    const inicioMin = toMin(horaInicio);
    const finMin = toMin(horaFin);
    return horarios.some(
      (t: any) =>
        inicioMin >= toMin(t.hora_inicio) && finMin <= toMin(t.hora_fin)
    );
  };

  const validarHoraPorDia = (horaInicio: string, horaFin: string): boolean => {
    const horarios = horariosEmpresa?.horarios || [];
    const diasSeleccionados = form.dias_semana;
    if (!diasSeleccionados.length)
      return validarHoraEnHorarioEmpresa(horaInicio, horaFin);
    const toMin = (h: string) => {
      const [hh, mm] = h.split(':').map(Number);
      return hh * 60 + mm;
    };
    const inicioMin = toMin(horaInicio);
    const finMin = toMin(horaFin);
    return diasSeleccionados.every((dia) => {
      const tramosDia = horarios.filter((t: any) => t.dia_semana === dia);
      if (!tramosDia.length) return false;
      return tramosDia.some(
        (t: any) =>
          inicioMin >= toMin(t.hora_inicio) && finMin <= toMin(t.hora_fin)
      );
    });
  };

  const removeHora = (idx: number) => {
    setHoras((prev) => prev.filter((_, i) => i !== idx));
  };

  // ============================================================
  // Return API del hook
  // ============================================================
  return {
    form,
    setForm,
    handleInput,
    toggleDay,

    precioAplicaOptions,
    loadingForm,

    horas,
    setHoras,
    newHora,
    setNewHora,
    addHora,
    removeHora,

    DAYS,

    // Categorías
    categoriasServicio,
    loadCategoriasServicio,
    refetchCategorias: loadCategoriasServicio,
    crearCategoriaServicio,

    // Imagen de categoría
    loadingImagen,
    uploadImagenCategoria,
    deleteImagenCategoria,

    // Info dialogs + toggles
    showAdvanced,
    setShowAdvanced,
    showDays,
    setShowDays,
    showInfo,
    setShowInfo,
    showInfoPublico,
    setShowInfoPublico,
    showInfoActivo,
    setShowInfoActivo,
    showInfoPermiteGrupos,
    setShowInfoPermiteGrupos,
    showInfoRequiereTrabajador,
    setShowInfoRequiereTrabajador,
    showInfoDiasDisponibles,
    setShowInfoDiasDisponibles,
    showInfoHorarioConcreto,
    setShowInfoHorarioConcreto,

    crearFasesAhora,
    setCrearFasesAhora,

    advancedRef,
    daysRef,
    horariosRef,

    getDescripcion,
    descripcionServicioDias,

    handleSubmit,
  };
}
